import { createContext, useContext, useEffect, useState } from 'react';
import Api, { Settings } from 'axiosApi/api';
import ErrorTemplateFoundPage from 'components/common/ErrorTemplatePage';
import globalAxios, { Axios, AxiosError } from 'axios';
import { useToastMessageQueue } from 'components/ToastMessages/ToastMessageProvider';
const ApiContext = createContext<Api | null>(null);
const ApiDataContext = createContext<{loginUrl: string, redirectUrl: string, urlWssNotifications: string}>({loginUrl: '', redirectUrl: '', urlWssNotifications: ''});

const SETTINGS_ENDPOINT = "https://3ksxno08b0.execute-api.us-east-1.amazonaws.com/prod/tenant-fe-config";

interface ImportMeta {
  env: {
    PROD: boolean;
    VITE_LOGIN_DOMAIN: string;
    VITE_RESPONSE_TYPE: string;
    VITE_CLIENT_ID: string;
    VITE_REDIRECT_URL: string;
    VITE_SIGNALR_HUB_URL: string; // Added SignalR hub URL
  };
}

export default function ApiProvider({ children }) {
  const [api, setApi] = useState<Api|null>(null);
  const [loginUrl, setLoginUrl] = useState<string|null>(null);
  const [redirectUrl, setRedirectUrl] = useState<string|null>(null);
  const [urlWssNotifications, setUrlWssNotifications] = useState<string|null>(null);
  const [error, setError] = useState<boolean>(false);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await fetch(SETTINGS_ENDPOINT);
        const remoteSettings: Settings = await response.json();
        const settings: Settings = {
          ...remoteSettings,
          ...(import.meta.env.DEV ? {
            URL_ADDRESS: import.meta.env.VITE_URL_ADDRESS || remoteSettings.URL_ADDRESS,
            URL_CLIENTS: import.meta.env.VITE_URL_CLIENTS || remoteSettings.URL_CLIENTS,
            URL_CONTACTINFO: import.meta.env.VITE_URL_CONTACTINFO || remoteSettings.URL_CONTACTINFO,
            URL_EMPLOYEE: import.meta.env.VITE_URL_EMPLOYEE || remoteSettings.URL_EMPLOYEE,
            URL_INVOICE: import.meta.env.VITE_URL_INVOICE || remoteSettings.URL_INVOICE,
            URL_JOBS: import.meta.env.VITE_URL_JOBS || remoteSettings.URL_JOBS,
            URL_LOCALE: import.meta.env.VITE_URL_LOCALE || remoteSettings.URL_LOCALE,
            URL_PROJECT: import.meta.env.VITE_URL_PROJECT || remoteSettings.URL_PROJECT,
            URL_REPORTS: import.meta.env.VITE_URL_REPORTS || remoteSettings.URL_REPORTS,
            URL_TIMEOFF: import.meta.env.VITE_URL_TIMEOFF || remoteSettings.URL_TIMEOFF,
            URL_TIMESHEET: import.meta.env.VITE_URL_TIMESHEET || remoteSettings.URL_TIMESHEET,
            URL_WORKFLOW: import.meta.env.VITE_URL_WORKFLOW || remoteSettings.URL_WORKFLOW,
            URL_NOTIFICATIONS: import.meta.env.VITE_URL_NOTIFICATIONS || remoteSettings.URL_NOTIFICATIONS,
            URL_WSS_NOTIFICATIONS: import.meta.env.VITE_URL_WSS_NOTIFICATIONS || remoteSettings.URL_WSS_NOTIFICATIONS,
          } : {}),
          LOGIN_DOMAIN: import.meta.env.PROD? remoteSettings.LOGIN_DOMAIN : import.meta.env.VITE_LOGIN_DOMAIN,
          CLIENT_ID: import.meta.env.PROD? remoteSettings.CLIENT_ID : import.meta.env.VITE_CLIENT_ID,
          REDIRECT_URL: import.meta.env.PROD? remoteSettings.REDIRECT_URL : import.meta.env.VITE_REDIRECT_URL,
          RESPONSE_TYPE: import.meta.env.PROD? remoteSettings.RESPONSE_TYPE : import.meta.env.VITE_RESPONSE_TYPE,
        };

        // Log the settings for debugging
        console.log('Loaded settings:', settings);
        
        const querySearchParams = new URLSearchParams({
          response_type: settings.RESPONSE_TYPE,
          client_id: settings.CLIENT_ID,
          redirect_uri: settings.REDIRECT_URL,
          state: 'STATE',
          scope: 'openid email'
        }).toString();
        
        const url = new URL('/login', settings.LOGIN_DOMAIN);
        url.search = querySearchParams;
        setLoginUrl(url.toString());
        setRedirectUrl(settings.REDIRECT_URL);
        setUrlWssNotifications(settings.URL_WSS_NOTIFICATIONS);
        setApi(new Api(settings));
      } catch (e) {
        console.error(e);
        setError(true);
      }
    };
    fetchSettings();
  }, []);
  globalAxios.interceptors.response.use((response)=>response, (error) => {
    // Handle "ERR_NETWORK" error
    const axiosError: AxiosError = error as AxiosError;
    return Promise.reject(error);
  });
  

  if(error) {
    return <ErrorTemplateFoundPage statusCode={500} title="Error" message={'Failed to load settings...'} />;
  }
  return (api &&
    <ApiContext.Provider value={api}>
      <ApiDataContext.Provider value={{loginUrl, redirectUrl, urlWssNotifications}}>
        {children}
      </ApiDataContext.Provider>
    </ApiContext.Provider>
  );
}

export function useApi() {
  return useContext(ApiContext);
}

export function useApiData() {
  return useContext(ApiDataContext);
}